/**
 * KATA Board V2 Display Mode
 * Single page read-only view showing all 8 components
 */

import React from 'react';
import { 
  Eye as EyeIcon,
  Edit as EditIcon,
  ArrowLeft as ArrowLeftIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Printer as PrinterIcon
} from 'lucide-react';

import { useKataBoardV2Store } from '../../stores/kataBoardV2Store';
import { navigationUtils } from '../../utils/kataBoardV2Utils';
import type { KATABoardV2 } from '../../types/kataBoardV2';

// Import the original components - we'll render them in read-only mode
import Component01_GenericInfo from './components/Component01_GenericInfo';
import Component02_03_CurrentTargetCondition from './components/Component02_03_CurrentTargetCondition';
import Component04_ProcessMap from './components/Component04_ProcessMap';
import Component05_ProcessObservations from './components/Component05_ProcessObservations';
import Component06_PDCACycles from './components/Component06_PDCACycles';
import Component07_IndicatorTrend from './components/Component07_IndicatorTrend';
import Component08_09_BeforeAfterStatus from './components/Component08_09_BeforeAfterStatus';
import Component10_Benefit from './components/Component10_Benefit';

interface KATABoardV2DisplayModeProps {
  kataBoard: KATABoardV2;
  onSwitchToEditMode?: () => void;
  onBack?: () => void;
}

const KATABoardV2DisplayMode: React.FC<KATABoardV2DisplayModeProps> = ({
  kataBoard,
  onSwitchToEditMode,
  onBack
}) => {
  const completionPercentage = navigationUtils.getCompletionPercentage(kataBoard);

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export functionality to be implemented');
  };

  const handleShare = () => {
    // TODO: Implement share functionality
    console.log('Share functionality to be implemented');
  };

  const components = [
    { 
      id: 1, 
      name: 'Project Charter', 
      component: <Component01_GenericInfo kataBoard={kataBoard} />,
      description: 'Workshop overview, team members, and objectives'
    },
    { 
      id: 2, 
      name: 'Current/Target Condition', 
      component: <Component02_03_CurrentTargetCondition kataBoard={kataBoard} />,
      description: 'Side-by-side comparison of current state and target condition with shared factors'
    },
    { 
      id: 3, 
      name: 'Process Map', 
      component: <Component04_ProcessMap kataBoard={kataBoard} />,
      description: 'Current process visualization and flow'
    },
    { 
      id: 4, 
      name: 'Process Observations', 
      component: <Component05_ProcessObservations kataBoard={kataBoard} />,
      description: 'Data collection and process observations'
    },
    { 
      id: 5, 
      name: 'PDCA Cycles', 
      component: <Component06_PDCACycles kataBoard={kataBoard} />,
      description: 'Plan-Do-Check-Act improvement cycles'
    },
    { 
      id: 6, 
      name: 'Indicator Trend', 
      component: <Component07_IndicatorTrend kataBoard={kataBoard} />,
      description: 'Key performance indicators and trends'
    },
    { 
      id: 7, 
      name: 'Before / After Status', 
      component: <Component08_09_BeforeAfterStatus kataBoard={kataBoard} />,
      description: 'Visual comparison of before and after states with multiple image support'
    },
    { 
      id: 8, 
      name: 'Benefit Analysis', 
      component: <Component10_Benefit kataBoard={kataBoard} />,
      description: 'Quantified benefits and impact measurement'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white">
      {/* Header - Hidden in print */}
      <div className="bg-white shadow-sm border-b print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Workshop Info */}
            <div className="flex items-center space-x-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Back
                </button>
              )}
              
              <div>
                <h1 className="text-xl font-semibold text-gray-900 flex items-center">
                  <EyeIcon className="h-5 w-5 mr-2 text-blue-600" />
                  KATA Board - {kataBoard.workshop_info?.workshop_number || 'Loading...'}
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Display Mode
                  </span>
                </h1>
                <p className="text-sm text-gray-500">
                  {kataBoard.workshop_info?.title || 'Loading workshop details...'}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              {/* Progress */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Completion:</span>
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${completionPercentage}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {completionPercentage}%
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrint}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  title="Print"
                >
                  <PrinterIcon className="h-4 w-4" />
                </button>
                
                <button
                  onClick={handleExport}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  title="Export"
                >
                  <DownloadIcon className="h-4 w-4" />
                </button>
                
                <button
                  onClick={handleShare}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  title="Share"
                >
                  <ShareIcon className="h-4 w-4" />
                </button>
                
                {onSwitchToEditMode && (
                  <button
                    onClick={onSwitchToEditMode}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <EditIcon className="h-4 w-4 mr-2" />
                    Switch to Edit Mode
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Header - Only visible in print */}
      <div className="hidden print:block print:mb-8">
        <div className="text-center border-b pb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            KATA Board - {kataBoard.workshop_info?.workshop_number}
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            {kataBoard.workshop_info?.title}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Generated on {new Date().toLocaleDateString()} • Completion: {completionPercentage}%
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:px-0 print:py-4">
        {/* Component Grid */}
        <div className="space-y-8 print:space-y-6">
          {components.map((comp, index) => {
            const isComplete = navigationUtils.isComponentComplete(kataBoard, comp.id);
            
            return (
              <div 
                key={comp.id} 
                className="bg-white rounded-lg shadow-sm border print:shadow-none print:border-2 print:break-inside-avoid"
                id={`component-${comp.id}`}
              >
                {/* Component Header */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 print:bg-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`
                        flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold
                        ${isComplete 
                          ? 'bg-green-500 text-white' 
                          : 'bg-gray-200 text-gray-600'
                        }
                      `}>
                        {comp.id}
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          {comp.name}
                        </h2>
                        <p className="text-sm text-gray-500">
                          {comp.description}
                        </p>
                      </div>
                    </div>
                    
                    {/* Completion Status */}
                    <div className="flex items-center space-x-2 print:hidden">
                      {isComplete ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Complete
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          In Progress
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Component Content */}
                <div className="p-6 print:p-4 kata-readonly-mode">
                  {comp.component}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer - Hidden in print */}
      <div className="bg-white border-t mt-12 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              KATA Board V2 • {components.length} Components • {completionPercentage}% Complete
            </div>
            
            {onSwitchToEditMode && (
            <button
              onClick={onSwitchToEditMode}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <EditIcon className="h-4 w-4 mr-2" />
              Edit This Board
            </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KATABoardV2DisplayMode;
